import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {PermissionId} from '../../../../../api';
import {PermissionQueryService} from './services/permission-query.service';
import {PermissionCommandService} from './services/permission-command.service';
import {PermissionMatrix} from './components/permission-matrix/permission-matrix';
import {PermissionRow} from './models/permission-row';

@Component({
  selector: 'elcia-permissions-settings',
  imports: [PermissionMatrix],
  providers: [PermissionQueryService, PermissionCommandService],
  templateUrl: './permissions-settings.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsSettings {
  private readonly queryService = inject(PermissionQueryService);
  private readonly commandService = inject(PermissionCommandService);

  protected readonly permissions = computed(() => this.queryService.permissions());
  protected readonly isLoading = computed(() => this.queryService.isLoading());

  // Working copy of the assignments; edits are persisted per user as soon as they change.
  protected readonly rows = signal<PermissionRow[]>([]);

  // Reseed the working copy whenever the server data changes (initial load and after a save).
  private readonly seedRows = effect(() => {
    const assignments = this.queryService.assignments();
    this.rows.set(assignments.map(a => ({
      userAccountId: a.userAccountId,
      fullName: a.fullName,
      permissions: [...a.permissions],
      isCurrentUser: a.isCurrentUser,
    })));
  });

  protected onToggle(event: {userAccountId: string; permission: PermissionId}): void {
    let updated: PermissionRow | undefined;

    this.rows.update(rows => rows.map(row => {
      if (row.userAccountId !== event.userAccountId) return row;
      const permissions = row.permissions.includes(event.permission)
        ? row.permissions.filter(p => p !== event.permission)
        : [...row.permissions, event.permission];
      updated = {...row, permissions};
      return updated;
    }));

    // Auto-save: persist the changed user immediately.
    if (updated) {
      this.commandService.updateMutation.mutate({
        userAccountId: updated.userAccountId,
        permissions: updated.permissions,
      });
    }
  }
}
